import React, { useEffect, useReducer } from 'react';
// import { useHistory, useLocation } from 'react-router';
import { useQuery } from '../utilities/hooks';
import { disciplineService} from './disciplineService';
import { IndexView, PaginationNavigator, OffCanvas } from '../common';
import { decodeSortString, formatSortString } from '../utilities/apiUtilities';
import DisciplineCreate from './DisciplineCreate';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';


const initialState = {
    isLoading: true,
    isSubmitting: true,
    lastApiCallTime: null,
    page: null,
    serverCommunication: null,
    indexParameters: {
        sortBy: '',
        isDescending: false,
        searchString: '',
        pageSize: 20,
        pageNumber: 1
    },
}

const ACTIONS = {
    DISCIPLINE_INDEX_CALL: 'DISCIPLINE_INDEX_CALL',
    DISCIPLINE_INDEX_SUCCESS: 'DISCIPLINE_INDEX_SUCCESS',
    DISCIPLINE_INDEX_ERROR: 'DISCIPLINE_INDEX_ERROR',
    UPDATE_PARAMETERS: 'UPDATE_PARAMETERS',
}

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.DISCIPLINE_INDEX_CALL:
            return {
                ...state,
                isLoading: true,
                lastApiCallTime: Date.now()
            };
        case ACTIONS.DISCIPLINE_INDEX_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isSubmitting: false,
                page: action.data
            };
        case ACTIONS.DISCIPLINE_INDEX_ERROR:
            return {
                ...state,
                isLoading: false,
                isSubmitting: false,
                page: null,
                serverCommunication: action.data
            }
        case ACTIONS.UPDATE_PARAMETERS:
            return {
                ...state,
                indexParameters: {
                    ...state.indexParameters,
                    ...action.data.indexParameters
                },
                isSubmitting: action.data.isSubmitting ?? false
            }
        default:
            throw new Error(`No action of type ${action.type} found in reducer.`)
    }
}

function DisciplineIndex() {

    const query = useQuery();
    // const history = useHistory();
    const location = useLocation();
    const navigate = useNavigate();

    const [state, dispatch] = useReducer(reducer, initialState, initializeState);

    const columnDefinitions = [
        {name: 'discipline_name', display: 'discipline_name', isSortable: true}
    ]

    useEffect(() => {
        let sortOrder = formatSortString(state.indexParameters.sortBy, state.indexParameters.isDescending);

        const queryParameters = new URLSearchParams({
            searchString: state.indexParameters.searchString,
            pageNumber: state.indexParameters.pageNumber,
            pageSize: state.indexParameters.pageSize,
            sortOrder: sortOrder
        });
        // history.replace({ pathname: location.pathname, search: queryParameters.toString()});

        navigate(location.pathname + '?' + queryParameters.toString(), { replace: true });


        if (state.isSubmitting) {
            dispatch({ type: ACTIONS.DISCIPLINE_INDEX_CALL });
            disciplineService.indexDisciplines({
                ...state.indexParameters,
                sortOrder: sortOrder
            })
            .then(response => dispatch({ type: ACTIONS.DISCIPLINE_INDEX_SUCCESS, data: response.data}))
            .catch(error => {

                dispatch({ type: ACTIONS.DISCIPLINE_INDEX_ERROR, data: []})

                toast.error("Error fetching disciplines");
                console.log(error)
            })
        }
    }, [state.isSubmitting, state.indexParameters, navigate, location.pathname]);

    function initializeState() {
        const [sortBy, isDescending] = decodeSortString(query.get('sortOrder'));
        
        return {
            ...initialState,
            indexParameters: {
                sortBy: sortBy,
                isDescending: isDescending,
                searchString: query.get('searchString') ?? '',
                pageSize: query.get('pageSize') ?? 20,
                pageNumber: query.get('pageNumber') ?? 1
            }
        }
    }

    function handleSortChange(value) {
        let sortBy = value;
        let isDescending = false;

        if (state.indexParameters.sortBy === sortBy && !state.indexParameters.isDescending) {
            sortBy = null
        } else if (state.indexParameters.sortBy !== sortBy) {
            isDescending = true
        }

        dispatch({ type: ACTIONS.UPDATE_PARAMETERS, data: { indexParameters: { sortBy: sortBy, isDescending: isDescending }, isSubmitting: true } });
    }

    function handleSearchStringChange(event) {
        dispatch({ type: ACTIONS.UPDATE_PARAMETERS, data: { indexParameters: { searchString: event.target.value } } });
    }

    function handleSearchFormSubmit(event) {
        event.preventDefault();
        dispatch({ type: ACTIONS.UPDATE_PARAMETERS, data: { indexParameters: { pageNumber: 1 }, isSubmitting: true } });
    }

    function handlePageSizeChange(event) {
        dispatch({ type: ACTIONS.UPDATE_PARAMETERS, data: { indexParameters: { pageSize: event.target.value } } });
    }

    function handlePageNumberChange(pageNumber) {
        dispatch({ type: ACTIONS.UPDATE_PARAMETERS, data: { indexParameters: { pageNumber: pageNumber }, isSubmitting: true } });
    }

    function handleItemClick(item) {

        // history.push(`/discipline/${item.id}`);

        navigate(`/discipline/${item.id}`);
    }

    function isLoading () {
        return state.isLoading && (Date.now() - state.lastApiCallTime) > 1000;
    }

    if (state.serverCommunication != null) {
        return (
            <div className='d-flex flex-fill align-items-center justify-content-center'>
                <p className='text-danger'>{state.serverCommunication}</p>
            </div>
        )
    }

    if (state.page == null || isLoading()) {
        return (
            <div className='d-flex flex-fill align-items-center justify-content-center'>
                <div className='spinner-border text-azure' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                </div>
            </div>
        )
    }

    return (
        <div className='container h-100 overflow-auto'>

        <OffCanvas id='newDisciplineForm' title='Create a Discipline'>
            <DisciplineCreate></DisciplineCreate>
        </OffCanvas>

        <h1>Disciplines</h1>
        <form className='row' onSubmit={handleSearchFormSubmit}>
            <div className='col'>
                <input type='text' className='form-control' placeholder='Filter shows by name' value={state.indexParameters.searchString} onChange={handleSearchStringChange}/>
            </div>
            <div className='col-auto'>
                <div className='input-group'>
                    <span className='input-group-text'>Page Size</span>
                    <input type='number' min='1' max='500' className='form-control' value={state.indexParameters.pageSize} onChange={handlePageSizeChange}/>
                </div>
            </div>
            <div className='col-auto'>
                <button className='btn btn-outline-azure' type='submit'>Retrieve</button>
            </div>
            <div className='col-auto'>
                <span className='vl-dark-navy'></span>
            </div>
            <div className='col-auto'>
                <button className='btn btn-azure' type='button' data-bs-toggle='offcanvas' data-bs-target='#newDisciplineForm'>
                    <i className='bi bi-plus-lg me-2'></i>
                    New
                </button>
            </div>
        </form>
        <IndexView columns={columnDefinitions} items={state.page.entities} onItemClick={handleItemClick} currentSortBy={state.indexParameters.sortBy} isCurrentSortDescending={state.indexParameters.isDescending} onSort={name => handleSortChange(name)}></IndexView>
        <PaginationNavigator data={state.page} onPageChange={handlePageNumberChange}></PaginationNavigator>
    </div>
    );
}

export default DisciplineIndex;